import { DestroyRef, Injectable, inject, signal } from "@angular/core";
import { Subscription } from "rxjs";
import { ASSET_TYPES } from "../../../enums/assetTypes.enum";
import { TIMEOUT_MS } from "../../../constants/timeout";
import { GateStatusAttribute, GateStatusObservation } from "../../../models/shared/gateStatus";
import { parseGateStatusAttribute } from "../../api/telemetry/gate-status.mapper";
import {
  AttributesSubscriptionHandle,
  AttributesWebsocketService,
} from "../../websocket/attributes-websocket/attributes-websocket.service";

/** Owns the selected gate's status, scoped to the consuming page. */
@Injectable()
export class GateStatusStateService {
  private readonly attributes = inject(AttributesWebsocketService);
  private readonly currentStatus = signal<GateStatusObservation | null | undefined>(null);
  private readonly statusTimeout = signal(false);
  private subscription?: Subscription;
  private handle?: AttributesSubscriptionHandle;
  private initialLoadTimer?: ReturnType<typeof setTimeout>;

  public readonly status = this.currentStatus.asReadonly();
  public readonly timeout = this.statusTimeout.asReadonly();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stop());
  }

  public load(gateSystemId: string): void {
    this.stop();
    this.currentStatus.set(null);
    this.statusTimeout.set(false);

    const showTimeout = () => {
      clearTimeout(this.initialLoadTimer);
      this.statusTimeout.set(true);
    };
    this.initialLoadTimer = setTimeout(showTimeout, TIMEOUT_MS);

    this.handle = this.attributes.register({
      entityType: ASSET_TYPES.GATE_SYSTEM,
      entityId: [gateSystemId],
      keys: ["gate_status"],
    });
    this.subscription = this.handle.messages$.subscribe({
      next: (message) => {
        if (message.entityType === ASSET_TYPES.GATE_SYSTEM && String(message.entityId) === gateSystemId) {
          const status = parseGateStatusAttribute(message.data.gate_status);
          if (status) {
            clearTimeout(this.initialLoadTimer);
            this.statusTimeout.set(false);
            this.applyStatus(status, message.delivery, gateSystemId);
          } else if (
            message.delivery === "snapshot" &&
            message.missingKeys?.includes("gate_status") &&
            this.currentStatus() === null
          ) {
            clearTimeout(this.initialLoadTimer);
            this.statusTimeout.set(false);
            this.currentStatus.set(undefined);
          }
        }
      },
      error: showTimeout,
      complete: showTimeout,
    });
  }

  private stop(): void {
    clearTimeout(this.initialLoadTimer);
    this.initialLoadTimer = undefined;
    this.subscription?.unsubscribe();
    this.subscription = undefined;
    this.handle?.close();
    this.handle = undefined;
  }

  private applyStatus(
    incoming: GateStatusAttribute,
    delivery: GateStatusObservation["delivery"],
    gateSystemId: string
  ): void {
    const current = this.currentStatus();
    if (current) {
      const timestamp = incoming.timestamp ?? -Infinity;
      const currentTimestamp = current.timestamp ?? -Infinity;
      if (timestamp < currentTimestamp) return;
      if (timestamp === currentTimestamp && current.delivery === "update") {
        if (delivery === "snapshot" || incoming.value === current.value) return;
      }
    }

    this.currentStatus.set({
      ...incoming,
      gateSystemId,
      delivery,
      ...(delivery === "update" && current ? { previousValue: current.value } : {}),
    });
  }
}
